<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { computed, inject, ref } from "vue"
import { useI18n } from "vue-i18n"
import { coreServiceRegistry } from "@core/services/register"
import { formatIdSource } from "@core/helpers"

// ================================
// TYPES & INTERFACES
// ================================

/** Réponse de création d'une demande de partage (champs utilisés) */
type CreateShareResponse = {
    _id?: string
    /** URL complète d’invitation (populateAndFormat métier) */
    url?: string
    consentement?: { _id?: string }
    source?: { _id?: string }
    token?: string
}

// ================================
// PROPS & EMITS
// ================================

const props = withDefaults(
    defineProps<{
        /** Identifiant unique de la source (compteur) */
        sourceId: string
        /** Identifiant réel du compteur (PDL ou PCE) */
        idsource: string
        /** Identifiant unique du bâtiment */
        buildingId: string
        /** true = parcours PCE, false = parcours PDL (pour les libellés) */
        isPce?: boolean
    }>(),
    { isPce: false }
)

const emit = defineEmits<{
    back: []
    sent: [data: CreateShareResponse]
}>()

// ================================
// SERVICES
// ================================

const injectedServices = inject<{
    consentRequest?: typeof coreServiceRegistry.consentRequest
}>("services")
const consentRequestService =
    injectedServices?.consentRequest ?? coreServiceRegistry.consentRequest

// ================================
// I18N
// ================================

const { t } = useI18n({
    messages: {
        fr: {
            title: "Alerte, ce compteur existe déjà",
            alert_message:
                "Le {type} {idsource} que vous essayez d'ajouter a déjà été enregistré dans l'application",
            demande:
                "Vous pouvez tout de même demander au titulaire de partager avec vous ses données.",
            button_return: "Retour",
            button_create: "Générer une demande de partage de données",
            success: "Demande envoyée",
        },
    },
})

// ================================
// STATE
// ================================

/** En cours d'envoi */
const isLoading = ref(false)
/** Message à afficher (succès ou erreur) */
const message = ref("")
/** true = erreur, false = succès */
const isError = ref(false)

// Timeout pour effacer le message après affichage (comportement legacy)
let messageTimeoutId: ReturnType<typeof setTimeout> | null = null

// ================================
// COMPUTED
// ================================

/** Type de compteur pour les libellés (PDL ou PCE). */
const getMeterTypeLabel = computed(() => (props.isPce ? "PCE" : "PDL"))

/** Identifiant formaté pour l'affichage. */
const getFormattedIdsource = computed(() =>
    formatIdSource(props.idsource, props.isPce ? "pce" : "pdl")
)

/** Message d'alerte avec type et identifiant. */
const getAlertMessage = computed(() =>
    t("alert_message", {
        type: getMeterTypeLabel.value,
        idsource: getFormattedIdsource.value,
    })
)

// ================================
// METHODS
// ================================

function clearMessageAfterDelay(): void {
    if (messageTimeoutId) clearTimeout(messageTimeoutId)
    messageTimeoutId = setTimeout(() => {
        message.value = ""
        isError.value = false
        messageTimeoutId = null
    }, 2000)
}

/** Émet le retour et remet le focus métier au parent. */
function onQuit(): void {
    emit("back")
}

/** Envoie la demande de partage puis émet sent avec la réponse. */
async function onSubmit(): Promise<void> {
    message.value = ""
    isError.value = false
    isLoading.value = true
    try {
        const data = await consentRequestService.createConsentRequestShare({
            sourceId: props.sourceId,
            buildingId: props.buildingId,
            mail: false,
        })
        message.value = t("success")
        isError.value = false
        clearMessageAfterDelay()
        emit("sent", data as CreateShareResponse)
    } catch (err: unknown) {
        const detailMessage =
            err && typeof err === "object" && "detail" in err
                ? (err as { detail?: { message?: string } }).detail?.message
                : undefined
        message.value =
            detailMessage ??
            "Erreur lors de la création de la demande de partage."
        isError.value = true
        clearMessageAfterDelay()
    } finally {
        isLoading.value = false
    }
}
</script>

<template>
    <div class="source-ask-sharing flex flex-col gap-4">
        <BaseMessage
            severity="info"
            :pt="{
                content: 'flex flex-col justify-center',
                text: 'flex flex-col gap-6',
            }"
        >
            <!-- Titre avec icône -->
            <div class="flex items-center justify-center gap-2">
                <i
                    class="pi pi-exclamation-circle text-xl"
                    aria-hidden="true"
                />
                <h3 class="text-lg font-semibold">
                    {{ t("title") }}
                </h3>
            </div>

            <!-- Message alerte -->
            <p class="text-center text-base">
                {{ getAlertMessage }}
            </p>

            <!-- Demande de partage -->
            <p class="text-center text-gray-600">
                {{ t("demande") }}
            </p>

            <!-- Message succès / erreur -->
            <div class="flex flex-col gap-4">
                <BaseMessage
                    v-if="message"
                    :severity="isError ? 'error' : 'success'"
                    class="w-full"
                >
                    {{ message }}
                </BaseMessage>
            </div>
        </BaseMessage>
        <div
            class="flex flex-col-reverse gap-3 md:flex-row md:flex-wrap md:justify-between"
        >
            <BaseButton
                :label="t('button_return')"
                severity="secondary"
                :disabled="isLoading"
                class="min-w-[10rem] w-full md:w-auto"
                @click="onQuit"
            />
            <BaseButton
                :label="t('button_create')"
                :loading="isLoading"
                :disabled="isLoading"
                class="min-w-[10rem] w-full md:w-fit"
                @click="onSubmit"
            />
        </div>
    </div>
</template>
